import { useCallback } from 'react'

interface UseFileTriggerProps {
  /**
   * Called when the files have been selected.
   *
   * @param files
   */
  onFilesSelected: (files: File[]) => void

  /**
   * The accepted file types.
   */
  acceptedFileTypes?: string[]

  /**
   * Whether multiple files are allowed.
   */
  allowMultiple?: boolean

  /**
   * Specifies the use of a media capture mechanism to capture the media on the spot.
   */
  defaultCamera?: 'user' | 'environment'
}

/**
 * Returns a function that triggers a file select.
 * Must be called as part of a user interaction.
 *
 * @param onFilesSelected
 * @param acceptedFileTypes
 * @param allowMultiple
 * @param defaultCamera
 */
export function useFileTrigger({
  onFilesSelected,
  acceptedFileTypes,
  allowMultiple,
  defaultCamera,
}: UseFileTriggerProps) {
  return useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = allowMultiple ?? false
    input.accept = acceptedFileTypes?.join(',') ?? '*/*'
    input.className = 'sr-only'

    if (defaultCamera) {
      input.capture = defaultCamera
    }

    input.onchange = () => {
      if (input.files?.length) {
        onFilesSelected(Array.from(input.files))
      }
      input.value = ''
      input.remove()
    }

    input.addEventListener('cancel', () => {
      input.value = ''
      input.remove()
    })

    document.body.appendChild(input)
    input.click()
  }, [allowMultiple, acceptedFileTypes, defaultCamera, onFilesSelected])
}
